import React from "react";
import { Box, Typography } from "@mui/material";

const StrategyStatItem = ({
  name,
  amount,
  percentage,
  positions,
  isNegative = false,
  isRisk = false,
  rank,
  isExpanded,
  onToggleExpand,
  details = [],
}) => {
  const truncatedName =
    name?.length > 20 ? `${name.substring(0, 20)}...` : name;

  return (
    <>
      <Box
        sx={{
          marginBottom: 1,
          display: "flex",
          alignItems: "flex-start",
          borderBottom: "1px solid #eee",
          paddingBottom: "8px",
          position: "relative",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#f8f9fa",
          },
        }}
        onClick={onToggleExpand}
      >
        {/* Rank circle */}
        <Box
          sx={{
            minWidth: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: isRisk ? "#ff4d4f15" : "#1a237e15",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "12px",
            marginTop: "2px",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: "bold",
              color: isRisk ? "#ff4d4f" : "#1a237e",
            }}
          >
            {rank}
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          {/* Left side - Name and Positions */}
          <Box
            sx={{
              maxWidth: "60%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                textAlign: "left",
              }}
              title={name}
            >
              {truncatedName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "0.75rem",
                display: "block",
                width: "100%",
                textAlign: "left",
              }}
            >
              {positions} positions
            </Typography>
          </Box>

          {/* Right side */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: "bold",
                color: isNegative ? "#ff4d4f" : "inherit",
              }}
            >
              {amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Typography>
            <Typography
              sx={{
                color: isNegative ? "#ff4d4f" : "#666",
                fontSize: "0.75rem",
              }}
            >
              {isRisk
                ? `${(percentage * 10000).toFixed(1)} bps`
                : `${percentage.toFixed(1)}% of AUM`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Expanded section */}
      {isExpanded && (
        <Box
          sx={{
            ml: 2,
            mb: 2,
            backgroundColor: "#fafafa",
            borderRadius: "4px",
            padding: "8px",
          }}
        >
          {details?.map((position, idx) => {
            const value = isRisk ? position.risk : position.marketValue;
            const percentValue = isRisk
              ? position.riskBps
              : position.percentOfNav;

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderBottom:
                    idx < details?.length - 1 ? "1px solid #eee" : "none",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                {/* Security Name Column */}
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    width: "70%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight: "4px",
                    textAlign: "left",
                  }}
                >
                  {position.securityName}
                </Typography>

                {/* Value Column */}
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    width: "30%",
                    textAlign: "right",
                    color: value < 0 ? "#ff4d4f" : "black",
                  }}
                >
                  {value < 0
                    ? `($${Math.abs(Math.round(value)).toLocaleString()})`
                    : `$${Math.round(value).toLocaleString()}`}
                </Typography>

                {/* Percentage Column */}
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    width: "30%",
                    textAlign: "right",
                    color: value < 0 ? "#ff4d4f" : "black",
                  }}
                >
                  {isRisk
                    ? `${value < 0 ? "" : "+"}${percentValue.toFixed(1)} bps`
                    : `${percentValue.toFixed(1)}%`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default StrategyStatItem;
